import { Settings } from "lucide-react"
import type { SessionType } from "./NewSessionDialog"
import { useI18n } from "../i18n"
import type { TranslationKey } from "../i18n/types"
import { Avatar } from "./Avatar"

type ConnectionStatus = "connected" | "switching" | "reconnecting" | "connecting"

interface ChatHeaderProps {
  connectionStatus: ConnectionStatus
  sessionType: SessionType | null
  onSettingsOpen: () => void
  /** PATH B SLICE 4A — active session's companion display name (e.g. "Rook"
   *  or "Rainer"), resolved server-side via resolveActiveCompanion(). Shown
   *  with its avatar so the header identifies which companion this session
   *  belongs to. Optional only for isolated component tests — AppShell's
   *  companionName prop defaults to "Rook", so in the real app this branch
   *  is never undefined once ChatHeader is mounted under AppShell. */
  companionName?: string
}

const STATUS_LABELS: Record<ConnectionStatus, string | null> = {
  connected: null,
  switching: "Switching...",
  reconnecting: "Reconnecting...",
  connecting: null,
}

export function ChatHeader({ connectionStatus, sessionType, onSettingsOpen, companionName }: ChatHeaderProps) {
  const { t } = useI18n()
  const isConnected = connectionStatus === "connected"
  const statusLabel = STATUS_LABELS[connectionStatus]

  return (
    <div className="chat-header">
      <div className="chat-header-title">
        {companionName && (
          <span className="chat-header-companion">
            <Avatar identity={companionName.toLowerCase()} size={20} />
            <span className="chat-header-companion-name">{companionName}</span>
          </span>
        )}
        <span className={`connection-dot ${isConnected ? "connected" : connectionStatus === "switching" ? "switching" : "disconnected"}`} />
        {statusLabel && <span className="connection-status-label">{statusLabel}</span>}
        {sessionType && (
          <span className={`session-badge session-badge-${sessionType}`}>
            {t(`session.${sessionType}` as TranslationKey)}
          </span>
        )}
      </div>
      <div className="chat-header-actions">
        <button
          className="btn btn-icon btn-ghost"
          onClick={onSettingsOpen}
          title="Settings"
          aria-label="Settings"
        >
          <Settings size={18} />
        </button>
      </div>
    </div>
  )
}
